/**
 * Utility to dynamically resolve API URLs based on LOCAL_WIP_FUNCTION environment variable
 * This allows pointing specific function app endpoints to local backends (e.g., localhost:7071, localhost:7074)
 * while keeping other functions pointing to the production/QA API
 *
 * Supports comma-separated lists:
 * LOCAL_WIP_FUNCTION=hal-hoss2-qa-time-cards-func-01/,hal-hoss2-qa-fiftythirteen-func-01/
 * LOCAL_HOST_URL=http://localhost:7071/,http://localhost:7074/
 */

/**
 * Gets the appropriate base URL and modified slug for an API endpoint
 * If LOCAL_WIP_FUNCTION is set and the endpoint belongs to one of those functions:
 *   - Returns the corresponding localhost URL from LOCAL_HOST_URL
 *   - Removes the function app name (including trailing slash) from the slug
 * Otherwise returns the standard HALLEN_API with original slug
 * @param slug - The API endpoint slug (e.g., "hal-hoss2-qa-time-cards-func-01/TimeCardsDropdowns")
 * @returns Object with baseUrl and modifiedSlug
 */
export function getApiUrlAndSlug(slug: string): { baseUrl: string; slug: string } {
  const localWipFunctions = process.env.LOCAL_WIP_FUNCTION
  const localHostUrls = process.env.LOCAL_HOST_URL

  // If no local WIP functions are configured, use standard API
  if (!localWipFunctions) {
    return {
      baseUrl: process.env.HALLEN_API || "",
      slug: slug
    }
  }

  // Parse comma-separated lists
  const functionList = localWipFunctions.split(",").map(f => f.trim())
  const hostList = localHostUrls ? localHostUrls.split(",").map(h => h.trim()) : []

  // Check if this endpoint belongs to any of the local WIP functions
  for (let i = 0; i < functionList.length; i++) {
    const functionName = functionList[i]
    if (slug.includes(functionName)) {
      // Remove the function app name (with trailing slash) from the slug
      const modifiedSlug = slug.replace(functionName, "")
      const baseUrl = hostList[i] ? `${hostList[i]}api/` : "http://localhost:7071/api/"
      return {
        baseUrl: baseUrl,
        slug: modifiedSlug
      }
    }
  }

  // Otherwise use standard API
  return {
    baseUrl: process.env.HALLEN_API || "",
    slug: slug
  }
}

/**
 * Gets the appropriate base URL and modified slug for an alternative API endpoint
 * Supports comma-separated lists of functions and host URLs
 * @param slug - The API endpoint slug
 * @returns Object with baseUrl and modifiedSlug
 */
export function getApiAltUrlAndSlug(slug: string): { baseUrl: string; slug: string } {
  const localWipFunctions = process.env.LOCAL_WIP_FUNCTION
  const localHostUrls = process.env.LOCAL_HOST_URL

  // If no local WIP functions are configured, use standard API ALT
  if (!localWipFunctions) {
    return {
      baseUrl: process.env.HALLEN_API_ALT || "",
      slug: slug
    }
  }

  // Parse comma-separated lists
  const functionList = localWipFunctions.split(",").map(f => f.trim())
  const hostList = localHostUrls ? localHostUrls.split(",").map(h => h.trim()) : []

  // Check if this endpoint belongs to any of the local WIP functions
  for (let i = 0; i < functionList.length; i++) {
    const functionName = functionList[i]
    if (slug.includes(functionName)) {
      // Remove the function app name (with trailing slash) from the slug
      const modifiedSlug = slug.replace(functionName, "")
      const baseUrl = hostList[i] ? `${hostList[i]}api/` : "http://localhost:7071/api/"
      return {
        baseUrl: baseUrl,
        slug: modifiedSlug
      }
    }
  }

  // Otherwise use standard API ALT
  return {
    baseUrl: process.env.HALLEN_API_ALT || "",
    slug: slug
  }
}

